import { useState } from 'react'
import type { AccessPayload } from '../types'
import { validateAccess } from '../services/api'

interface Props {
  onSuccess: (access: AccessPayload) => void
  onTeacher: () => void
}

export function LoginScreen({ onSuccess, onTeacher }: Props) {
  const [code, setCode] = useState('')
  const [website, setWebsite] = useState('')
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setError('')
    const cleanCode = code.trim().toUpperCase()
    if (!/^[0-9]{4}[A-Z]?$/.test(cleanCode)) {
      setError('Escribe los cuatro números que aparecen en el papel entregado por tu profesora.')
      return
    }
    if (!acceptedPrivacy) {
      setError('Marca la casilla del aviso de privacidad para comenzar.')
      return
    }
    try {
      setLoading(true)
      onSuccess(await validateAccess(cleanCode, website))
    } catch {
      setError('El código no coincide. Revisa el papel entregado por tu profesora e inténtalo nuevamente.')
    } finally { setLoading(false) }
  }

  return (
    <main id="main-content" className="landing-page v3-landing">
      <section className="hero-card v3-hero">
        <img className="hero-logo" src="/assets/logo-ihlc.png" alt="Logotipo del Instituto de Humanidades Luis Campino" />
        <div className="hero-copy">
          <p className="eyebrow">Instituto de Humanidades Luis Campino</p>
          <h1>Laboratorio de Historia</h1>
          <p className="hero-subtitle">Una práctica guiada para comprender, analizar y reforzar la Historia de Chile.</p>
          <ol className="access-steps" aria-label="Pasos para ingresar">
            <li><span>1</span>Busca el papel con tu código.</li>
            <li><span>2</span>Escribe los cuatro números.</li>
            <li><span>3</span>Comienza la sesión preparada para ti.</li>
          </ol>
        </div>
      </section>

      <form className="login-card v3-login" onSubmit={submit} noValidate>
        <p className="step-label">Acceso individual</p>
        <h2>Escribe tus cuatro números</h2>
        <p className="login-instruction">Usa el código entregado por tu profesora. No necesitas escribir tu nombre ni elegir una actividad.</p>
        <label htmlFor="access-code">Código personal</label>
        <input
          id="access-code" className="code-input" inputMode="numeric" value={code}
          onChange={(event) => setCode(event.target.value.replace(/[^0-9A-Za-z]/g, '').slice(0, 5))}
          autoComplete="one-time-code" placeholder="0000" required autoFocus aria-describedby="code-help"
        />
        <small id="code-help">Escribe exactamente los cuatro números entregados por tu profesora.</small>
        <label className="honeypot" aria-hidden="true">Sitio web<input value={website} onChange={(event) => setWebsite(event.target.value)} tabIndex={-1} autoComplete="off" /></label>

        <details className="privacy-notice compact-details">
          <summary>¿Qué información se guardará?</summary>
          <p>El código carga tu nombre, curso y sesión. Los resultados se guardan de manera privada para que tu profesora pueda acompañar tu aprendizaje.</p>
        </details>
        <label className="checkbox-label privacy-check" htmlFor="privacy-accepted">
          <input id="privacy-accepted" type="checkbox" checked={acceptedPrivacy} onChange={(event) => setAcceptedPrivacy(event.target.checked)} />
          Comprendí el aviso y deseo comenzar.
        </label>
        {error && <p className="error-message" role="alert">{error}</p>}
        <button className="primary-button hero-action" type="submit" disabled={loading}>{loading ? 'Buscando tu sesión…' : 'Ingresar a mi sesión'}</button>
        <button className="text-button teacher-entry" type="button" onClick={onTeacher}>Acceso de la profesora</button>
      </form>
    </main>
  )
}
